import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpApi from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { UI_LANGUAGE_CODES } from "./shared/utils/languages.js";
import { buildInfo } from "./shared/buildInfo";

// ?lang= comes from the office portal and is authoritative: this origin's
// localStorage is partitioned when we are framed, so its copy can be stale or
// absent, and the LANGUAGE_CHANGE postMessage only arrives after first paint.
// Same contract as ?theme= (see main.jsx). Runs before init() on purpose —
// this module is import-hoisted above all app code.
try {
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (urlLang && UI_LANGUAGE_CODES.includes(urlLang)) {
    localStorage.setItem("language", urlLang);
  }
} catch { /* SSR/no-storage: detector falls through to navigator */ }

i18n
  .use(HttpApi)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: UI_LANGUAGE_CODES,
    // A key missing from the generated Cyrillic locale shows Latin Uzbek,
    // never English (scripts/gen-uz-cyrl.mjs keeps the gap at zero).
    fallbackLng: { "uz-Cyrl": ["uz", "en"], default: ["en"] },
    debug: false,
    ns: [
      "common",
      "navigation",
      "my-docs",
      "mainPage",
      "signing",
      "shared",
      "sidebar",
      "apiKey",
      "shareModal",
      "version",
      "table",
      "pagination",
      "editor",
      "errors",
      "assistant",
      "console",
      "records",
      "search",
    ],
    defaultNS: "common",
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      // The catalogs are plain files, so a browser that cached one keeps it
      // after a deploy and shows raw keys for every string added since. The
      // build changes with each image, so a build never reuses an old copy.
      queryStringParams: { v: buildInfo.build },
    },
    detection: {
      // No htmlTag: index.html hardcodes lang="en", which used to pin every
      // fresh session to English before navigator was ever consulted.
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "language",
    },
    react: {
      useSuspense: true,
      bindI18n: 'languageChanged',
    },
  });

// Keep <html lang> honest (a11y, hyphenation, extension heuristics).
i18n.on("languageChanged", (lng) => {
  try {
    document.documentElement.lang = lng;
  } catch { /* non-DOM env */ }
});

export default i18n;
